import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    UserPlus,
    X,
    GripVertical,
    ArrowUp,
    ArrowDown,
    CheckCircle2,
    Clock,
    Loader2,
    Users,
    Trash2,
    Save,
    Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { usePostRequest, useDeleteRequest, usePatchRequest } from '@/shared/hooks/requests';
import {
    WORKFLOW,
    WORKFLOW_PARTICIPANT,
    WORKFLOW_PARTICIPANTS,
} from '@/shared/utils/urls';
import { participantIdLabel } from '../participants';

/**
 * CandidatesManager — Dialog for managing signer candidates.
 * Shows current candidates, allows add/remove/reorder.
 */
export const CandidatesManager = ({
    open,
    onOpenChange,
    documentId,
    signerCollection,
    onRefresh,
    currentUser,
    document,
}) => {
    const { t, i18n } = useTranslation();

    // Local state for new candidate form
    const [newPinfl, setNewPinfl] = useState('');
    const [newFullName, setNewFullName] = useState('');
    const [addingCandidate, setAddingCandidate] = useState(false);

    // Hooks
    const addCandidateReq = usePostRequest();
    const deleteCandidateReq = useDeleteRequest();
    const patchCandidateReq = usePatchRequest();

    const candidates = signerCollection?.participants || [];
    const signProcedureType = signerCollection?.procedureType || signerCollection?.procedure_type || 'sequential';
    const isProcedureCompleted = signerCollection?.isCompleted || signerCollection?.is_completed || false;
    const initiatorId = signerCollection?.initiatorUserId || signerCollection?.initiator_user_id;
    const ownerId = document?.ownerId || document?.owner_id;

    // Permissions check - allow both initiator and owner to edit
    const isInitiator = currentUser?.id === initiatorId;
    const isOwner = currentUser?.id === ownerId;
    const hasAnySignature = candidates.some(c => c.status === 'signed');
    const canEdit = (isInitiator || isOwner) && !hasAnySignature && !isProcedureCompleted;

    const handleAddCandidate = async () => {
        if (!newPinfl.trim()) {
            toast.error(t('signing:addSigners.noSigners'));
            return;
        }

        if (newPinfl.trim().length !== 14) {
            toast.error(t('signing:addSigners.invalidPinfl', { pinfl: newPinfl }));
            return;
        }

        setAddingCandidate(true);
        try {
            const res = await addCandidateReq.request({
                url: WORKFLOW_PARTICIPANTS.replace('{file_id}', documentId),
                data: {
                    participants: [{
                        nationalIdValue: newPinfl.trim(),
                        fullName: newFullName.trim() || null,
                        participantOrder: candidates.length,
                    }],
                },
            });

            if (res.success) {
                toast.success(t('signing:candidates.added'));
                setNewPinfl('');
                setNewFullName('');
                if (onRefresh) onRefresh();
            } else {
                toast.error(t('signing:candidates.addError'));
            }
        } catch (err) {
            console.error(err);
            toast.error(t('signing:candidates.addError'));
        } finally {
            setAddingCandidate(false);
        }
    };

    const handleDeleteCandidate = async (candidateId) => {
        try {
            const res = await deleteCandidateReq.request({
                url: WORKFLOW_PARTICIPANTS.replace('{file_id}', documentId),
                data: {
                    participantIds: [candidateId],
                },
            });

            if (res.success) {
                toast.success(t('signing:candidates.removed'));
                if (onRefresh) onRefresh();
            } else {
                toast.error(t('signing:candidates.removeError'));
            }
        } catch (err) {
            console.error(err);
            toast.error(t('signing:candidates.removeError'));
        }
    };

    const handleUpdateProcedureType = async (newType) => {
        try {
            const res = await patchCandidateReq.request({
                url: WORKFLOW.replace('{file_id}', documentId),
                data: {
                    procedureType: newType,
                },
            });

            if (res.success) {
                if (onRefresh) onRefresh();
            } else {
                toast.error(t('signing:candidates.updateError'));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleUpdateOrder = async (candidateId, newOrder) => {
        try {
            const res = await patchCandidateReq.request({
                url: WORKFLOW_PARTICIPANT
                    .replace('{file_id}', documentId)
                    .replace('{participant_id}', candidateId),
                data: {
                    participantOrder: newOrder,
                },
            });

            if (res.success) {
                if (onRefresh) onRefresh();
            } else {
                toast.error(t('signing:candidates.updateError'));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const moveCandidate = (index, direction) => {
        const candidate = candidates[index];
        const swapIndex = index + direction;
        if (swapIndex < 0 || swapIndex >= candidates.length) return;

        const swapCandidate = candidates[swapIndex];

        // Swap orders
        handleUpdateOrder(candidate.id, swapCandidate.participantOrder ?? swapCandidate.participant_order ?? swapIndex);
        handleUpdateOrder(swapCandidate.id, candidate.participantOrder ?? candidate.participant_order ?? index);
    };

    const sortedCandidates = [...candidates].sort((a, b) => {
        const orderA = a.participantOrder ?? a.participant_order ?? 0;
        const orderB = b.participantOrder ?? b.participant_order ?? 0;
        return orderA - orderB;
    });

    const isLoading = addCandidateReq.loading || deleteCandidateReq.loading || patchCandidateReq.loading;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto border-none bg-background shadow-2xl ring-1 ring-border/50">
                <DialogHeader>
                    <DialogTitle className="text-lg font-bold flex items-center gap-2">
                        <span className="w-7 h-7 rounded-md bg-brand/10 flex items-center justify-center shrink-0"><Users className="w-4 h-4 text-brand" /></span>
                        {t('signing:candidates.title')}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    {/* Procedure type selection */}
                    <div className="flex items-center justify-between bg-muted/30 p-2 rounded-lg border border-border">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-muted-foreground">{t('signing:mode.label')}:</span>
                            <Select
                                value={signProcedureType}
                                onValueChange={handleUpdateProcedureType}
                                disabled={!canEdit || isLoading}
                            >
                                <SelectTrigger className="w-[140px] h-8 text-xs bg-background">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="sequential" className="text-xs">
                                        {t('signing:mode.sequential')}
                                    </SelectItem>
                                    <SelectItem value="parallel" className="text-xs">
                                        {t('signing:mode.parallel')}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {!canEdit && !isProcedureCompleted && (
                            <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                                {!isInitiator && !isOwner ?
                                    t('signing:candidates.onlyInitiator') :
                                    (hasAnySignature ? t('signing:candidates.alreadySigned') : '')
                                }
                            </div>
                        )}

                        {isProcedureCompleted && (
                            <Badge className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                                {t('signing:candidates.completed')}
                            </Badge>
                        )}
                    </div>

                    {/* Candidates list */}
                    <div className="space-y-2">
                        {sortedCandidates.length === 0 ? (
                            <div className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-lg border border-dashed border-border">
                                {t('signing:candidates.empty')}
                            </div>
                        ) : (
                            sortedCandidates.map((candidate, idx) => {
                                const pinfl = participantIdLabel(candidate);
                                const isSigned = candidate.status === 'signed';
                                const order = candidate.participantOrder ?? candidate.participant_order ?? idx;
                                const signerName = candidate.signature?.certificate?.subjectCommonName
                                    || candidate.signature?.certificate?.subject_common_name
                                    || candidate.fullName
                                    || candidate.full_name
                                    || null;

                                return (
                                    <div
                                        key={candidate.id}
                                        className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${isSigned
                                            ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                                            : 'bg-card border-border hover:border-brand/40'
                                            }`}
                                    >
                                        {/* Order controls (only for sequential and editable) */}
                                        {signProcedureType === 'sequential' && canEdit && (
                                            <div className="flex flex-col gap-0.5 shrink-0">
                                                <button
                                                    onClick={() => moveCandidate(idx, -1)}
                                                    disabled={idx === 0 || isLoading}
                                                    className="p-0.5 rounded hover:bg-muted disabled:opacity-30 transition-colors"
                                                >
                                                    <ArrowUp className="w-3 h-3" />
                                                </button>
                                                <button
                                                    onClick={() => moveCandidate(idx, 1)}
                                                    disabled={idx === sortedCandidates.length - 1 || isLoading}
                                                    className="p-0.5 rounded hover:bg-muted disabled:opacity-30 transition-colors"
                                                >
                                                    <ArrowDown className="w-3 h-3" />
                                                </button>
                                            </div>
                                        )}

                                        {/* Order number */}
                                        {signProcedureType === 'sequential' && (
                                            <div className="w-6 h-6 rounded-full bg-brand/10 flex items-center justify-center shrink-0">
                                                <span className="text-xs font-bold text-brand dark:text-[#6FA4F9]">{order + 1}</span>
                                            </div>
                                        )}

                                        {/* Status icon */}
                                        <div className="shrink-0">
                                            {isSigned ? (
                                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                            ) : (
                                                <Clock className="w-4 h-4 text-yellow-500" />
                                            )}
                                        </div>

                                        {/* Candidate info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium truncate">
                                                    {signerName || pinfl || '\u2014'}
                                                </span>
                                            </div>
                                            {signerName && pinfl && (
                                                <span className="text-xs text-muted-foreground font-mono">
                                                    {t('signing:candidates.pinfl')}: {pinfl}
                                                </span>
                                            )}
                                            {isSigned && candidate.signature?.signedAt && (
                                                <div className="text-xs text-green-600 mt-0.5">
                                                    {t('signing:eimzo.signedAt')}: {new Date(candidate.signature.signedAt).toLocaleString(i18n.language)}
                                                </div>
                                            )}
                                        </div>

                                        {/* Delete button (only if editable) */}
                                        {!isSigned && canEdit && (
                                            <button
                                                onClick={() => handleDeleteCandidate(candidate.id)}
                                                disabled={isLoading}
                                                className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-muted-foreground hover:text-red-500 transition-colors shrink-0"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Add new candidate form (only if editable) */}
                    {canEdit && (
                        <div className="border-t border-border pt-4 space-y-3">
                            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <UserPlus className="w-3.5 h-3.5" />
                                {t('signing:candidates.addNew')}
                            </h4>

                            <div className="space-y-2">
                                <Input
                                    placeholder={t('signing:addSigners.pinflPlaceholder')}
                                    value={newPinfl}
                                    onChange={(e) => setNewPinfl(e.target.value.replace(/\D/g, '').slice(0, 14))}
                                    className="font-mono"
                                    maxLength={14}
                                />
                                <Input
                                    placeholder={t('signing:addSigners.namePlaceholder')}
                                    value={newFullName}
                                    // Names: letters, numbers and spaces only — strip anything else.
                                    onChange={(e) => setNewFullName(e.target.value.replace(/[^\p{L}\p{N} ]/gu, ''))}
                                />
                                <Button
                                    onClick={handleAddCandidate}
                                    disabled={addingCandidate || newPinfl.trim().length !== 14}
                                    className="w-full"
                                    variant="outline"
                                >
                                    {addingCandidate ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Plus className="w-4 h-4 mr-2" />
                                    )}
                                    {t('signing:candidates.add')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CandidatesManager;
